/**
 * Everything an admin can change, in one place.
 *
 * The field list is not written here. `SETTING_DEFS` in `@proxmon/shared` is
 * the same table the server validates against and the environment importer
 * migrates from, so a setting cannot exist in one of those three places and be
 * missing from the others. This module owns the shell: which section is open,
 * what has been edited but not saved, and the two panes — endpoints and the
 * account — that are lists rather than fields.
 *
 * Drafts are text until Save. Coercion happens once, through the shared
 * `coerceSetting`, so a number typed here is clamped by exactly the rule the
 * server would have applied to it.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  MIN_PASSWORD_LENGTH,
  SETTING_DEFS,
  SETTING_SECTIONS,
  coerceSetting,
  type SettingDef,
  type SettingValue,
  type SettingsPatch,
} from '@proxmon/shared';
import { useStore } from '../state/store.ts';
import { ApiTokens } from './ApiTokens.tsx';
import { EndpointsSection } from './EndpointsSection.tsx';
import { SettingsField, type Draft } from './SettingsField.tsx';

/** The rail: the registry's sections, plus the two that are not field lists. */
type PaneId = (typeof SETTING_SECTIONS)[number]['id'] | 'endpoints' | 'account';

const PANES: Array<{ id: PaneId; title: string; blurb: string }> = [
  ...SETTING_SECTIONS.map((s) => ({ id: s.id as PaneId, title: s.title, blurb: s.blurb })),
  { id: 'endpoints', title: 'Endpoints', blurb: 'The Proxmox APIs Proxmon polls, and how it authenticates.' },
  { id: 'account', title: 'Access', blurb: 'Your password, and the tokens other programs hold.' },
];

/** The stored value, as the text (or checkbox state) the form edits. */
function toDraft(def: SettingDef, value: SettingValue | undefined): Draft {
  if (def.kind === 'boolean') return value === true;
  // A secret is never sent to us, so its box always starts empty; typing in it
  // is what marks it for saving.
  if (def.kind === 'secret') return '';
  if (Array.isArray(value)) return value.join(', ');
  if (value === null || value === undefined) return '';
  return String(value);
}

export function SettingsDialog({ onClose }: { onClose: () => void }) {
  const settings = useStore((s) => s.settings);
  const updateSettings = useStore((s) => s.updateSettings);
  const testAlerts = useStore((s) => s.testAlerts);
  const changePassword = useStore((s) => s.changePassword);
  const notify = useStore((s) => s.notify);

  const [pane, setPane] = useState<PaneId>('general');
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [pwError, setPwError] = useState<string | null>(null);

  const baseline = useMemo(() => {
    const out: Record<string, Draft> = {};
    for (const def of SETTING_DEFS) out[def.key] = toDraft(def, settings?.values[def.key]);
    return out;
  }, [settings]);

  // Re-seed when the server's view changes, but keep whatever is being typed:
  // a save elsewhere in the dialog must not wipe a half-finished field.
  useEffect(() => {
    setDraft((current) => {
      const merged = { ...baseline };
      for (const key of touched) if (key in current) merged[key] = current[key] as Draft;
      return merged;
    });
    // `touched` deliberately not a dependency: this reacts to server data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseline]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const secretsSet = useMemo(() => new Set(settings?.secretsSet ?? []), [settings]);

  /**
   * What would be sent. A non-secret is included when its text differs from
   * what came back from the server; a secret only when it has been typed in,
   * because an untouched secret box is empty and sending that would clear a
   * perfectly good credential.
   */
  const patch = useMemo((): SettingsPatch => {
    const out: SettingsPatch = {};
    for (const def of SETTING_DEFS) {
      const value = draft[def.key];
      if (value === undefined) continue;
      if (def.kind === 'secret') {
        if (touched.has(def.key)) out[def.key] = value;
        continue;
      }
      if (value !== baseline[def.key]) out[def.key] = coerceSetting(def, value) ?? value;
    }
    return out;
  }, [draft, baseline, touched]);

  const dirty = Object.keys(patch).length > 0;

  const set = (key: string, value: Draft) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setTouched((current) => new Set(current).add(key));
  };

  const save = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      await updateSettings(patch);
      setTouched(new Set());
      notify('info', 'Settings saved.');
    } catch (err) {
      notify('error', err instanceof Error ? err.message : 'Could not save those settings.');
    } finally {
      setBusy(false);
    }
  };

  const revert = () => {
    setDraft(baseline);
    setTouched(new Set());
  };

  const sendTestCard = async () => {
    setBusy(true);
    try {
      const result = await testAlerts();
      if (result.ok) notify('info', 'Card posted. It should be in the channel now.');
      else notify('error', result.error ?? 'The webhook did not accept the card.');
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setPwError(null);
    if (next.length < MIN_PASSWORD_LENGTH) {
      setPwError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setPwOpen(false);
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  const active = PANES.find((p) => p.id === pane) ?? PANES[0]!;

  /** Fields of the open section, minus the ones their `showWhen` hides. */
  const fields = SETTING_DEFS.filter((def) => {
    if (def.section !== pane) return false;
    if (!def.showWhen) return true;
    const gate = draft[def.showWhen];
    return gate !== undefined && gate !== false && gate !== '';
  });

  return (
    <div className="overlay" onPointerDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="dialog wide" role="dialog" aria-modal="true" aria-label="Settings">
        <header>
          <h2>Settings</h2>
          <button type="button" className="close" style={{ marginLeft: 'auto' }} onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        {!settings ? (
          <div className="body">
            <p className="empty">Loading…</p>
          </div>
        ) : (
          <div className="settings-layout">
            <nav className="settings-rail" aria-label="Settings sections">
              {PANES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={p.id === pane ? 'active' : ''}
                  aria-current={p.id === pane}
                  onClick={() => setPane(p.id)}
                >
                  {p.title}
                </button>
              ))}
            </nav>

            <div className="settings-pane body">
              <div className="pane-head">
                <h3>{active.title}</h3>
                <p>{active.blurb}</p>
              </div>

              {settings.imported && pane === 'general' && (
                <div className="warn-box">
                  Settings were imported out of <code>.env</code> into the database on this start. The dialog
                  is now the place to change them — the file is only read again for settings that have never
                  been stored here.
                </div>
              )}

              {settings.pendingRestart.length > 0 && (
                <div className="warn-box">
                  Saved, but not in force until Proxmon restarts:{' '}
                  <strong>{settings.pendingRestart.join(', ')}</strong>.
                </div>
              )}

              {pane === 'endpoints' ? (
                <EndpointsSection />
              ) : pane === 'account' ? (
                <>
                  <ApiTokens />
                  <div className="setting-row" style={{ display: 'block' }}>
                    <div className="text" style={{ marginBottom: 8 }}>
                      <strong>Password</strong>
                      <span>Changing it signs out every other device.</span>
                    </div>
                    {pwOpen ? (
                      <form onSubmit={submitPassword}>
                        {pwError && <div className="form-error">{pwError}</div>}
                        <div className="field">
                          <label htmlFor="current-password">Current password</label>
                          <input
                            id="current-password"
                            type="password"
                            value={current}
                            autoComplete="current-password"
                            onChange={(event) => setCurrent(event.target.value)}
                          />
                        </div>
                        <div className="field">
                          <label htmlFor="new-password">New password</label>
                          <input
                            id="new-password"
                            type="password"
                            value={next}
                            autoComplete="new-password"
                            onChange={(event) => setNext(event.target.value)}
                          />
                          <div className="hint">At least {MIN_PASSWORD_LENGTH} characters.</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button type="submit" className="btn primary" disabled={busy}>
                            Change password
                          </button>
                          <button type="button" className="btn ghost" onClick={() => setPwOpen(false)}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <button type="button" className="btn" onClick={() => setPwOpen(true)}>
                        Change password…
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {fields.map((def) => (
                    <SettingsField
                      key={def.key}
                      def={def}
                      value={draft[def.key] ?? toDraft(def, settings.values[def.key])}
                      secretSet={secretsSet.has(def.key)}
                      secretTouched={touched.has(def.key)}
                      disabled={busy}
                      onChange={(value) => set(def.key, value)}
                    />
                  ))}

                  {pane === 'alerts' && (
                    <div className="pane-extra">
                      <button
                        type="button"
                        className="btn"
                        disabled={busy || !secretsSet.has('teamsWebhook')}
                        onClick={() => void sendTestCard()}
                        title={
                          secretsSet.has('teamsWebhook')
                            ? 'Post a sample card to the channel'
                            : 'Save a webhook first'
                        }
                      >
                        Send a test card
                      </button>
                      <span className="hint">Uses the saved webhook, so save first if you just pasted one.</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className="foot">
          {dirty && <span className="dirty-note">{Object.keys(patch).length} unsaved</span>}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            {dirty && (
              <button type="button" className="btn ghost" disabled={busy} onClick={revert}>
                Revert
              </button>
            )}
            <button type="button" className="btn primary" disabled={busy || !dirty} onClick={() => void save()}>
              Save changes
            </button>
            <button type="button" className="btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
